/**
 * export-pdf.js — client-side "export dashboard as PDF".
 *
 * Captures the live dashboard DOM (so the PDF matches exactly what the user
 * sees — same KPI cards, charts, tables, filters, theme) and paginates it
 * across A4 pages. Interactive-only chrome (action buttons, drill pills,
 * toolbars, open menus) is stripped in the cloned document via the
 * `[data-pdf-exclude]` marker, so it never affects the live UI.
 *
 * Pagination is "card-aware": we collect the vertical offsets of every
 * `.dash-card` (KPI tiles, chart + table cards) and prefer to cut pages at
 * those gaps, so widgets are not sliced in half across a page break. A hard
 * fallback cut is used only when a single card is taller than one page.
 *
 * Libraries (html2canvas + jspdf) are dynamically imported so they stay out
 * of the initial bundle and only load when the user actually exports.
 */

// A4 portrait in points (72 dpi): 595.28 x 841.89.
const A4 = { w: 595.28, h: 841.89 };
const PAGE_MARGIN = 24; // pt — breathing room around the content on each page.
const CAPTURE_SCALE = 2; // 2x for crisp text/charts in the PDF.

function resolveBackgroundColor(el) {
    // Walk up until we find a non-transparent background so dark-mode exports
    // get a dark page (readable text) and light-mode gets white.
    let node = el;
    while (node && node !== document.documentElement) {
        const bg = getComputedStyle(node).backgroundColor;
        if (bg && bg !== "transparent" && !bg.startsWith("rgba(0, 0, 0, 0)")) {
            return bg;
        }
        node = node.parentElement;
    }
    const bodyBg = getComputedStyle(document.body).backgroundColor;
    return bodyBg && bodyBg !== "transparent" ? bodyBg : "#ffffff";
}

/**
 * Compute canvas-space y positions (in captured px) that are SAFE to cut at —
 * the bottom edge of each top-level card plus the content bottom.
 */
function collectBreakpoints(rootEl, canvasHeight, cssToCanvas) {
    const rootTop = rootEl.getBoundingClientRect().top;
    const points = new Set([0]);
    rootEl.querySelectorAll(".dash-card, [data-pdf-break]").forEach((card) => {
        // Skip hidden / excluded nodes.
        if (card.closest("[data-pdf-exclude]")) return;
        const rect = card.getBoundingClientRect();
        if (rect.height <= 0) return;
        const bottomCss = rect.bottom - rootTop;
        points.add(Math.round(bottomCss * cssToCanvas));
    });
    points.add(canvasHeight);
    return Array.from(points).filter((p) => p >= 0 && p <= canvasHeight).sort((a, b) => a - b);
}

/**
 * Greedy, card-aware pagination. Returns an array of {start, height} slices
 * (in captured px) that each fit within one PDF page's drawable height.
 */
function planPages(breakpoints, canvasWidth, canvasHeight, pageDrawablePx) {
    const slices = [];
    let start = 0;
    let guard = 0;
    while (start < canvasHeight - 1 && guard < 5000) {
        guard += 1;
        const hardLimit = start + pageDrawablePx;
        if (hardLimit >= canvasHeight) {
            slices.push({ start, height: canvasHeight - start });
            break;
        }
        // Largest breakpoint that still fits within this page, and is beyond start.
        let cut = -1;
        for (const bp of breakpoints) {
            if (bp > start && bp <= hardLimit) cut = bp;
            else if (bp > hardLimit) break;
        }
        // No card boundary fits (a single card taller than a page) → hard cut.
        if (cut <= start) cut = hardLimit;
        slices.push({ start, height: cut - start });
        start = cut;
    }
    return slices;
}

/**
 * Wait until the dashboard is visually settled before we rasterise it:
 *  • web fonts finished loading (JetBrains Mono / Inter) — otherwise html2canvas
 *    captures fallback glyphs with the wrong metrics, which both garbles KPI
 *    numbers and shifts their baseline inside the card;
 *  • two animation frames so any pending layout/paint is committed;
 *  • a short settle delay so chart animations and KPI number counters finish.
 */
async function waitForRenderReady(timeoutMs = 4000) {
    try {
        if (document.fonts && document.fonts.ready) {
            await Promise.race([
                document.fonts.ready,
                new Promise((r) => setTimeout(r, timeoutMs)),
            ]);
        }
    } catch (_) { /* fonts API unavailable — continue */ }
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    await new Promise((r) => setTimeout(r, 550));
}

/**
 * Export a dashboard DOM subtree to a downloaded PDF.
 * @param {HTMLElement} rootEl - element wrapping the full dashboard content.
 * @param {string} dashboardName - used for the file name + caller's toast.
 * @returns {Promise<void>} resolves when the file has been generated.
 */
export async function exportDashboardToPdf(rootEl, dashboardName) {
    if (!rootEl) throw new Error("Nothing to export");

    const [{ default: html2canvas }, jspdfMod] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
    ]);
    const JsPDF = jspdfMod.jsPDF || jspdfMod.default;

    const backgroundColor = resolveBackgroundColor(rootEl);

    // Capture at the dashboard's EXACT current rendered width and freeze it, so
    // html2canvas evaluates the same responsive breakpoint as the live view and
    // the grid cannot reflow (no column count / spacing changes in the PDF).
    const captureWidth = Math.round(rootEl.getBoundingClientRect().width) || rootEl.scrollWidth;

    // Ensure fonts + charts + counters are fully rendered before rasterising.
    await waitForRenderReady();

    const canvas = await html2canvas(rootEl, {
        scale: CAPTURE_SCALE,
        backgroundColor,
        useCORS: true,
        logging: false,
        scrollX: 0,
        scrollY: 0,
        width: captureWidth,
        windowWidth: captureWidth,
        windowHeight: rootEl.scrollHeight,
        onclone: (clonedDoc, clonedRoot) => {
            // Freeze the exact desktop width + box model on the export copy so
            // the layout is pixel-identical to the live dashboard.
            if (clonedRoot) {
                clonedRoot.style.width = `${captureWidth}px`;
                clonedRoot.style.maxWidth = "none";
                clonedRoot.style.margin = "0";
            }
            // Kill transitions/animations so animated KPI counters and chart
            // tweens are captured at their final, settled state.
            const freeze = clonedDoc.createElement("style");
            freeze.textContent = "*,*::before,*::after{transition:none !important;animation:none !important;}";
            clonedDoc.head.appendChild(freeze);
            // Strip interactive-only chrome from the EXPORT copy only.
            clonedDoc.querySelectorAll("[data-pdf-exclude]").forEach((el) => {
                el.style.display = "none";
            });
            // Collapse any open menus/popovers/tooltips that are portalled.
            clonedDoc
                .querySelectorAll('[role="menu"], [role="tooltip"], [data-radix-popper-content-wrapper]')
                .forEach((el) => { el.style.display = "none"; });
            // Neutralise sticky positioning so docked filter bars render inline
            // (sticky offsets can otherwise overlap content in the flattened image).
            clonedDoc.querySelectorAll(".sticky").forEach((el) => {
                el.style.position = "static";
            });
        },
    });

    const cssWidth = captureWidth || canvas.width / CAPTURE_SCALE;
    const cssToCanvas = canvas.width / cssWidth;

    const pdf = new JsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const drawableW = A4.w - PAGE_MARGIN * 2;
    const drawableH = A4.h - PAGE_MARGIN * 2;

    // How many captured px map to one drawable page height (keep aspect ratio:
    // the full canvas width maps to drawableW).
    const pxPerPage = Math.floor((canvas.width * drawableH) / drawableW);

    const breakpoints = collectBreakpoints(rootEl, canvas.height, cssToCanvas);
    const slices = planPages(breakpoints, canvas.width, canvas.height, pxPerPage);

    slices.forEach((slice, i) => {
        if (i > 0) pdf.addPage();
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = slice.height;
        const ctx = pageCanvas.getContext("2d");
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(
            canvas,
            0, slice.start, canvas.width, slice.height,
            0, 0, canvas.width, slice.height,
        );
        const imgData = pageCanvas.toDataURL("image/jpeg", 0.92);
        const renderH = (slice.height * drawableW) / canvas.width;
        pdf.addImage(imgData, "JPEG", PAGE_MARGIN, PAGE_MARGIN, drawableW, renderH, undefined, "FAST");
    });

    const safeName = (dashboardName || "dashboard").replace(/[\\/:*?"<>|]+/g, "_").trim() || "dashboard";
    pdf.save(`${safeName}.pdf`);
}
